import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
  inject,
  OnInit,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, map, scan, shareReplay, startWith } from 'rxjs';
import { CoinDictionaryService } from './coin-dictionary.service';
import { MultiselectComponent } from '@ui-lib/components';
import { IDictionaryItem } from '../../types/dictionary-item.interface';

@Component({
  selector: 'mon-coin-selector',
  standalone: true,
  imports: [AsyncPipe, TranslateModule, FormsModule, MultiselectComponent, ReactiveFormsModule],
  templateUrl: './coin-selector.component.html',
  styleUrl: './coin-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CoinDictionaryService],
})
export class CoinSelectorComponent implements OnInit {
  protected readonly coinDictionaryService = inject(CoinDictionaryService);
  private readonly destroyRef = inject(DestroyRef);

  @Input()
  public controlEntity: FormControl<number[]> = new FormControl([], { nonNullable: true });

  @Input()
  public displayIcons = true;

  @Output()
  public readonly selectionChange: EventEmitter<IDictionaryItem<number>[]> = new EventEmitter<
    IDictionaryItem<number>[]
  >();

  protected readonly coins$ = this.coinDictionaryService.source$.pipe(
    scan(
      (
        acc: IDictionaryItem<number>[],
        newbies: IDictionaryItem<number>[],
      ): IDictionaryItem<number>[] => [...acc, ...newbies],
      [],
    ),
    map((items: IDictionaryItem<number>[]): IDictionaryItem<number>[] => {
      const seenIds = new Set<number>();

      return items.filter((item: IDictionaryItem<number>): boolean => {
        if (item.id === undefined) return false;

        if (seenIds.has(item.id)) {
          return false;
        }

        seenIds.add(item.id);

        return true;
      });
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public ngOnInit(): void {
    combineLatest([
      this.coins$,
      this.controlEntity.valueChanges.pipe(startWith(this.controlEntity.value)),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([items, selectedIds]) => {
        const selectedSet = new Set(selectedIds ?? []);
        const selectedItems = items.filter(
          (item) => item.id !== undefined && selectedSet.has(item.id),
        );
        this.selectionChange.emit(selectedItems);
      });
  }
}
